/**
 * useDanismanlarOptions
 *
 * Danışman select'lerinde pasif politikası — TEK yerden uygulanır (bkz.
 * proje-yoneticisi görev talimatı, 2026-08-13 — İş C).
 *
 * KURAL: danışman listesi HER ZAMAN `aktif=tumu` ile çekilir — hiçbir yerde
 * `aktif=true` KULLANILMAZ. Sebep: bir lead/satış/kural zaten pasif bir
 * danışmana bağlıysa, select o mevcut değeri göstermeyi KESMEMELİDİR — aksi
 * halde kullanıcı mevcut atamayı görmeden farkında olmadan değiştirir/bozar.
 * Pasif danışmanlar listeden ÇIKARILMAZ, yalnızca:
 *   - Etiketlerine "(Pasif)" rozeti eklenir (bkz. danismanOptionLabel).
 *   - Atama amaçlı select'lerde `disablePasif: true` ile seçilemez yapılır
 *     (mevcut değer GÖRÜNÜR kalır, YENİ atama engellenir).
 *   - Filtreleme amaçlı select'lerde `disablePasif` kullanılmaz (geçmiş veri
 *     filtrelenebilir kalmalı).
 *
 * Kullanılan yerler: AdminLeads.jsx (form + toplu + satır içi atama),
 * DatalarBlock.jsx (filtreleme), DanismanCockpitShared.jsx → LeadDetayModal
 * (yeniden atama), AdminPrimKurallari.jsx (prim kuralı — seçilebilir kalır),
 * AdminStudentSalePage.jsx (manuel satış — atama amaçlı).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useDanismanlarOptions(authHeaders, { includeAdmins = false, enabled = true } = {}) {
  const [danismanlar, setDanismanlar] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const fetchDanismanlar = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ aktif: 'tumu' });
      if (includeAdmins) params.set('include_admins', 'true');
      const r = await axios.get(`${API}/admin/danismanlar?${params.toString()}`, { headers: authHeaders() });
      setDanismanlar(r.data.danismanlar || []);
    } catch {
      setDanismanlar([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAdmins, enabled]);

  useEffect(() => { fetchDanismanlar(); }, [fetchDanismanlar]);

  return { danismanlar, loading, refetch: fetchDanismanlar };
}

/** Select <option> etiketi: ad + (admin) + (Pasif) rozeti. */
export function danismanOptionLabel(d, tr) {
  const isAdmin = d.role === 'admin' || d.role === 'superadmin';
  let label = d.name;
  if (isAdmin) label += ' (admin)';
  if (d.danisman_aktif === false) label += ` ${tr('(Pasif)', '(Inactive)')}`;
  return label;
}

/**
 * <option> render listesini üretir — {key, value, label, disabled}.
 * disablePasif=true: atama amaçlı select'lerde pasif danışmanlar seçilemez
 * (mevcut değer YİNE render edilir, yalnızca yeni seçim engellenir).
 * disablePasif=false (varsayılan): filtreleme amaçlı select'lerde pasifler
 * de normal seçilebilir kalır.
 */
export function danismanOptionsList(danismanlar, tr, { disablePasif = false } = {}) {
  return (danismanlar || []).map((d) => ({
    key: d.user_id,
    value: d.user_id,
    label: danismanOptionLabel(d, tr),
    disabled: disablePasif && d.danisman_aktif === false,
  }));
}
